import React, { useMemo, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { MemoryPlace, MemoryConnection } from '../../services/memoryService';
import './MemoryPalace.css';

interface MemoryPalaceGraphProps {
  places: MemoryPlace[];
  connections: MemoryConnection[];
  onPlaceClick: (placeId: number) => void;
}

interface GraphNode {
  id: number;
  name: string;
  description?: string;
  is_root?: boolean;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: number | GraphNode;
  target: number | GraphNode;
  description?: string;
}

const MemoryPalaceGraph: React.FC<MemoryPalaceGraphProps> = ({
  places,
  connections,
  onPlaceClick
}) => {
  const graphRef = useRef<any>();
  const hasInitializedRef = useRef(false);

  // Calculate shortest path distance from root to each node using BFS
  const calculateNodeDistances = (nodes: GraphNode[], links: GraphLink[], rootId: number): Map<number, number> => {
    const distances = new Map<number, number>();
    const visited = new Set<number>();
    const queue: { nodeId: number; distance: number }[] = [{ nodeId: rootId, distance: 0 }];
    
    distances.set(rootId, 0);
    visited.add(rootId);

    // Build adjacency list for faster lookup
    const adjacencyList = new Map<number, number[]>();
    links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      if (!adjacencyList.has(sourceId)) {
        adjacencyList.set(sourceId, []);
      }
      if (!adjacencyList.has(targetId)) {
        adjacencyList.set(targetId, []);
      }
      adjacencyList.get(sourceId)!.push(targetId);
      adjacencyList.get(targetId)!.push(sourceId); // Undirected graph
    });

    // BFS to calculate distances
    while (queue.length > 0) {
      const { nodeId, distance } = queue.shift()!;
      const neighbors = adjacencyList.get(nodeId) || [];
      
      for (const neighborId of neighbors) {
        if (!visited.has(neighborId)) {
          visited.add(neighborId);
          distances.set(neighborId, distance + 1);
          queue.push({ nodeId: neighborId, distance: distance + 1 });
        }
      }
    }

    // Set distance for disconnected nodes (not reachable from root)
    nodes.forEach(node => {
      if (!distances.has(node.id)) {
        distances.set(node.id, Infinity); // Very far away
      }
    });

    return distances;
  };

  // Transform data into graph format with root node fixed at center
  const graphData = useMemo(() => {
    // Create new node objects (clones) to avoid mutating dependencies
    const nodes: GraphNode[] = places.map(place => ({
      id: place.id,
      name: place.name,
      description: place.description,
      is_root: place.is_root
    }));

    const links: GraphLink[] = connections.map(conn => ({
      source: conn.from_place_id,
      target: conn.to_place_id,
      description: conn.description
    }));

    // Find root node or use first node as fallback
    const rootNode = nodes.find(node => !!node.is_root) || nodes[0];
    
    if (!rootNode) {
      return { nodes, links };
    }

    // Fix root node at center (0, 0)
    rootNode.fx = 0;
    rootNode.fy = 0;
    rootNode.x = 0;
    rootNode.y = 0;

    // Calculate distances from root to all nodes
    const distances = calculateNodeDistances(nodes, links, rootNode.id);
    
    // Group nodes by level (distance from root)
    const nodesByLevel = new Map<number, GraphNode[]>();
    nodes.forEach(node => {
      if (node.id === rootNode.id) return; // Skip root, already positioned
      
      const distance = distances.get(node.id) ?? Infinity;
      const level = distance === Infinity ? 999 : distance; // Put disconnected nodes at end
      
      if (!nodesByLevel.has(level)) {
        nodesByLevel.set(level, []);
      }
      nodesByLevel.get(level)!.push(node);
    });

    // Initialize nodes in hierarchical/radial layout
    const baseRadius = 200; // Base distance from root for level 1
    const radiusIncrement = 180; // Additional radius per level
    const angleSpread = Math.PI * 2; // Full circle

    nodesByLevel.forEach((levelNodes, level) => {
      if (level === 999) {
        // Disconnected nodes: place them far away in a ring
        const disconnectedRadius = baseRadius + radiusIncrement * 5;
        levelNodes.forEach((node, index) => {
          const angle = (index / levelNodes.length) * angleSpread;
          node.x = Math.cos(angle) * disconnectedRadius;
          node.y = Math.sin(angle) * disconnectedRadius;
        });
      } else {
        // Connected nodes: place in concentric circles
        const radius = baseRadius + radiusIncrement * level;
        levelNodes.forEach((node, index) => {
          const angle = (index / levelNodes.length) * angleSpread;
          node.x = Math.cos(angle) * radius;
          node.y = Math.sin(angle) * radius;
        });
      }
    });

    return { nodes, links };
  }, [places, connections]);

  // Configure force simulation to work with hierarchical layout
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const graph = graphRef.current;
      
      // Configure link force - maintain connections but allow flexibility
      const linkForce = graph.d3Force('link');
      if (linkForce) {
        linkForce.distance(120); // Distance between directly connected nodes
        linkForce.strength(0.5); // Moderate link strength to maintain hierarchy
      }
      
      // Configure charge force - moderate repulsion to prevent clustering
      const chargeForce = graph.d3Force('charge');
      if (chargeForce) {
        chargeForce.strength(-300); // Moderate repulsion between nodes
      }
      
      // Add centering force to keep graph centered (weak, mainly for disconnected nodes)
      const centerForce = graph.d3Force('center');
      if (centerForce) {
        centerForce.strength(0.05); // Very weak centering force
      }
      
      // Restart the simulation to apply changes
      graph.d3ReheatSimulation();
    }
  }, [graphData.nodes.length, graphData.links.length]);

  // Center view after initial layout
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
      const graph = graphRef.current;
      
      // Wait for graph to initialize, then center view with padding for nested levels
      const timer = setTimeout(() => {
        graph.zoomToFit(400, 50); // Add padding to ensure all levels are visible
        hasInitializedRef.current = true;
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [graphData.nodes.length]);

  // Handle node click
  const handleNodeClick = (node: GraphNode) => {
    onPlaceClick(node.id);
  };

  if (places.length === 0) {
    return (
      <div className="memory-palace-graph-empty">
        <p>No places to display. Create places to see them in the graph view.</p>
      </div>
    );
  }

  return (
    <div className="memory-palace-graph-container">
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        nodeId="id"
        linkDirectionalArrowLength={6}
        linkDirectionalArrowRelPos={1}
        linkDirectionalArrowColor="#666666"
        onNodeClick={handleNodeClick}
        onNodeDrag={(node: GraphNode) => {
          // Prevent root node from being dragged away from center
          if (node.is_root) {
            node.fx = 0;
            node.fy = 0;
          }
        }}
        onNodeDragEnd={(node: GraphNode) => {
          // Keep root fixed at center, allow others to move freely
          if (node.is_root) {
            node.fx = 0;
            node.fy = 0;
          }
          // Non-root nodes remain draggable without fixed position
        }}
        // Force simulation configuration
        cooldownTicks={150}
        onEngineStop={() => {
          // Re-center view after initial layout to include all nodes
          if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
            setTimeout(() => {
              if (graphRef.current) {
                // Use padding to ensure all nodes are visible, including nested levels
                graphRef.current.zoomToFit(400, 50);
                hasInitializedRef.current = true;
              }
            }, 100);
          }
        }}
        // Custom node rendering with labels below nodes
        nodeCanvasObject={(node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
          
          const label = node.name;
          const fontSize = Math.max(12 / globalScale, 8);
          const radius = 12;
          
          // Draw circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          ctx.fillStyle = node.is_root ? '#dc3545' : '#ffffff';
          ctx.fill();
          ctx.strokeStyle = node.is_root ? '#721c24' : '#333333';
          ctx.lineWidth = node.is_root ? 3 : 2;
          ctx.stroke();
          
          // Draw label below node
          ctx.font = `${fontSize}px 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#333333';
          ctx.fillText(label, node.x, node.y + radius + 5);
        }}
        nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

          const pointerAreaRadius = 16; // Make clickable area larger than visual
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, pointerAreaRadius, 0, 2 * Math.PI, false);
          ctx.fill();
        }}
        // Custom link rendering with labels and arrows
        linkCanvasObject={(link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
          const source = link.source as GraphNode;
          const target = link.target as GraphNode;
          
          if (typeof source.x !== 'number' || typeof source.y !== 'number' || typeof target.x !== 'number' || typeof target.y !== 'number') return;
          
          // Calculate arrow position (near target node, accounting for node radius)
          const nodeRadius = 12;
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const unitX = dx / distance;
          const unitY = dy / distance;
          
          // Arrow head position (at target node edge)
          const arrowLength = 8;
          const arrowX = target.x - unitX * nodeRadius;
          const arrowY = target.y - unitY * nodeRadius;
          
          // Draw line (stop before arrow)
          ctx.beginPath();
          ctx.moveTo(source.x, source.y);
          ctx.lineTo(arrowX, arrowY);
          ctx.strokeStyle = '#e0e0e0';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw arrow head
          const angle = Math.atan2(dy, dx);
          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle - Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle + Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fillStyle = '#666666';
          ctx.fill();
          
          // Draw label if description exists
          if (link.description) {
            const midX = (source.x + target.x) / 2;
            const midY = (source.y + target.y) / 2;
            const fontSize = Math.max(12 / globalScale, 8);
            ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#666666';
            // Add background for better readability
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 4;
            ctx.strokeText(link.description, midX, midY);
            ctx.fillText(link.description, midX, midY);
          }
        }}
        // Zoom and pan controls
        enableZoomInteraction={true}
        enablePanInteraction={true}
        minZoom={0.1}
        maxZoom={3}
      />
      
      {/* Zoom controls */}
      <div className="memory-palace-graph-zoom-controls">
        <button
          className="memory-palace-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() * 1.2, 400);
            }
          }}
          title="Zoom In"
        >
          +
        </button>
        <button
          className="memory-palace-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() / 1.2, 400);
            }
          }}
          title="Zoom Out"
        >
          −
        </button>
        <button
          className="memory-palace-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoomToFit(400, 50); // Include padding to show all nested levels
            }
          }}
          title="Reset Zoom / Fit to Screen"
        >
          ⌂
        </button>
      </div>
    </div>
  );
};

export default MemoryPalaceGraph;
